import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { NotebookPen, Download, Sparkles, CheckCircle2, Undo2, Loader2, RefreshCw, Upload, AlertTriangle, FileUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { API_BASE } from '../../config/api';
import { downloadModuleNotes } from '../../lib/studyGuide';

const API = `${API_BASE}/api`;

const STATUS = {
  generating: { label: 'Writing notes…', cls: 'bg-sky-500/15 text-sky-300' },
  draft: { label: 'Awaiting approval', cls: 'bg-amber-500/15 text-amber-300' },
  approved: { label: 'Approved · visible to learners', cls: 'bg-green-500/15 text-green-400' },
  failed: { label: 'Failed', cls: 'bg-red-500/15 text-red-300' },
};

function NotesRow({ row, busy, onGenerate, onApprove, onRevoke, onDownload, onUpload }) {
  const n = row.notes;
  const status = n ? STATUS[n.status] : null;
  const ready = n && (n.status === 'draft' || n.status === 'approved');
  const fileRef = useRef(null);
  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-4 rounded-lg border border-zinc-800 bg-zinc-900/60" data-testid={`study-notes-row-${row.module_id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium truncate">{row.title}</p>
        <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-zinc-500">
          <span>{row.week_count} week{row.week_count === 1 ? '' : 's'} · {row.course_count} learning unit{row.course_count === 1 ? '' : 's'}</span>
          {status && <Badge className={status.cls} data-testid={`study-notes-status-${row.module_id}`}>{status.label}</Badge>}
          {n?.source === 'upload' && <Badge className="bg-indigo-500/15 text-indigo-300" data-testid={`study-notes-source-${row.module_id}`}><FileUp className="h-3 w-3 mr-1" />Uploaded: {n.filename}</Badge>}
          {n?.source === 'ai' && n?.generated_at && <span>AI-written {new Date(n.generated_at).toLocaleDateString()}{n.auto_generated ? ' (on publish)' : ''}</span>}
          {n?.status === 'failed' && <span className="text-red-300 truncate max-w-xs" title={n.error}>{n.error}</span>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {n?.status === 'generating' ? (
          <Button size="sm" disabled className="bg-zinc-800 text-zinc-300"><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Writing</Button>
        ) : (
          <Button size="sm" variant="outline" disabled={busy || row.course_count === 0} onClick={() => onGenerate(row)}
            className="border-zinc-700 text-zinc-200" title={row.course_count === 0 ? 'Add a learning unit to this module first' : 'AI writes notes from every week in this module'}
            data-testid={`study-notes-generate-${row.module_id}`}>
            {n ? <RefreshCw className="h-4 w-4 mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            {n ? 'Regenerate' : 'Generate now'}
          </Button>
        )}
        <input ref={fileRef} type="file" accept=".docx,.pdf" className="hidden" data-testid={`study-notes-file-${row.module_id}`}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onUpload(row, f); }} />
        <Button size="sm" variant="outline" disabled={busy || n?.status === 'generating'} onClick={() => fileRef.current?.click()} className="border-zinc-700 text-zinc-200" data-testid={`study-notes-upload-${row.module_id}`}>
          <Upload className="h-4 w-4 mr-1.5" />Upload
        </Button>
        {ready && (
          <>
            <Button size="sm" variant="outline" onClick={() => onDownload(row)} className="border-zinc-700 text-zinc-200" data-testid={`study-notes-download-${row.module_id}`}>
              <Download className="h-4 w-4 mr-1.5" />Download
            </Button>
            {n.status === 'draft' ? (
              <Button size="sm" disabled={busy} onClick={() => onApprove(row)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`study-notes-approve-${row.module_id}`}>
                <CheckCircle2 className="h-4 w-4 mr-1.5" />Approve
              </Button>
            ) : (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => onRevoke(row)} className="border-zinc-700 text-zinc-300" data-testid={`study-notes-revoke-${row.module_id}`}>
                <Undo2 className="h-4 w-4 mr-1.5" />Unpublish
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function StudyNotesTab({ programmeId, token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/programmes/${programmeId}/study-notes`, { headers });
      setRows(res.data.modules || []);
    } catch (e) {
      toast.error('Failed to load study notes');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeId, token]);

  useEffect(() => { load(); }, [load]);

  const anyGenerating = rows.some((r) => r.notes?.status === 'generating');
  useEffect(() => {
    if (!anyGenerating) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [anyGenerating, load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const base = (row) => `${API}/programmes/${programmeId}/modules/${row.module_id}/study-notes`;
  const onGenerate = (row) => act(() => axios.post(`${base(row)}/generate`, {}, { headers }), 'Writing study notes — this takes a minute or two');
  const onApprove = (row) => act(() => axios.post(`${base(row)}/approve`, {}, { headers }), 'Study notes approved — learners can now download them');
  const onRevoke = (row) => act(() => axios.post(`${base(row)}/revoke`, {}, { headers }), 'Study notes hidden from learners');
  const onDownload = (row) => downloadModuleNotes(programmeId, row.module_id, token).catch((e) => toast.error(e.message));
  const onUpload = (row, file) => {
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!['.docx', '.pdf'].includes(ext)) return toast.error('Only Word (.docx) or PDF files can be uploaded');
    if (file.size > 25 * 1024 * 1024) return toast.error('File must be 25 MB or smaller');
    const form = new FormData();
    form.append('file', file);
    return act(() => axios.post(`${base(row)}/upload`, form, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }),
      'Study notes uploaded — approve them to show learners');
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800" data-testid="study-notes-tab">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2"><NotebookPen className="h-5 w-5 text-teal-400" />Study Notes</CardTitle>
        <p className="text-sm text-zinc-400">
          One set of study notes per module, written by NAVI from every week's learning units. They are created automatically when you publish a module.
          Download to edit in Word, upload your version, then approve. Learners always receive a branded, watermarked PDF — so upload your final version as a PDF (Word uploads are for admin drafts only).
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-zinc-500 text-sm">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-zinc-500 text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Add a module to the programme first.</p>
        ) : rows.map((row) => (
          <NotesRow key={row.module_id} row={row} busy={busy}
            onGenerate={onGenerate} onApprove={onApprove} onRevoke={onRevoke} onDownload={onDownload} onUpload={onUpload} />
        ))}
      </CardContent>
    </Card>
  );
}
